import { Link } from 'react-router-dom';

interface Doctor {
  name: string;
  nameCn: string;
  specialty: string;
  title: string;
}

interface Hotel {
  name: string;
  nameCn: string;
  distance: string;
  priceRange: string;
  rating: string;
}

const SampleGuide = () => {
  const doctor: Doctor = {
    name: 'Dr. Zhang Wei',
    nameCn: '张伟医生',
    specialty: 'Cardiology',
    title: 'Chief Physician'
  };

  const hotels: Hotel[] = [
    {
      name: 'Sheraton Beijing Dongcheng Hotel',
      nameCn: '北京东城喜来登酒店',
      distance: '0.8 km',
      priceRange: '¥800-¥1,200 / night',
      rating: '4.8/5'
    },
    {
      name: 'The Peninsula Beijing',
      nameCn: '北京半岛酒店',
      distance: '1.2 km',
      priceRange: '¥2,000-¥3,000 / night',
      rating: '4.9/5'
    },
    {
      name: 'Beijing Hotel NUO',
      nameCn: '北京诺金酒店',
      distance: '1.5 km',
      priceRange: '¥600-¥900 / night',
      rating: '4.7/5'
    }
  ];

  return (
    <div className="container-custom max-w-4xl py-8">
      <div className="mb-8">
        <Link
          to="/services"
          className="text-primary-600 hover:text-primary-700 mb-4 inline-block"
        >
          ← Back to Services
        </Link>
        <h1 className="text-4xl font-bold mb-4">Sample Medical Guide</h1>
          <p className="text-gray-600 text-lg">
            This is a sample of the bilingual medical guide you will receive. Your personalized guide will be tailored to your specific needs and preferences.
          </p>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-600 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-semibold text-yellow-800 mb-1">Sample Guide</h3>
              <p className="text-sm text-yellow-700">
                This is a sample guide. Your actual guide will be customized based on your medical condition, preferred city, and specific requirements.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-8">
          <div className="bg-primary-600 text-white px-6 py-4">
            <h2 className="text-2xl font-bold">医院信息 / Hospital Information</h2>
          </div>
          <div className="p-6">
            <div className="mb-6">
              <h3 className="font-semibold text-lg mb-2">Beijing Anzhen Hospital / 北京安贞医院</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Hospital Level / 医院等级:</p>
                  <p className="font-medium">3A Grade / 三甲医院</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Certifications / 认证:</p>
                  <p className="font-medium">JCI Certified</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">International Department / 国际部:</p>
                  <p className="font-medium text-green-600">Available / 有</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Languages Supported / 语言支持:</p>
                  <p className="font-medium">English, Chinese / 英文, 中文</p>
                </div>
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="font-semibold text-lg mb-3">Hospital Address / 医院地址</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600">Address / 地址:</p>
                  <p className="font-medium">No. 2 Anzhen Road, Chaoyang District, Beijing / 北京市朝阳区安贞路2号</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Coordinates / 坐标:</p>
                  <p className="font-medium">39.9748° N, 116.4183° E</p>
                </div>
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="font-semibold text-lg mb-3">Transportation Cost / 交通费用</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">From Beijing Capital Airport (PEK) / 从首都机场:</span>
                    <span className="font-semibold">¥150-¥200 (US$20-27)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">From Beijing Daxing Airport (PKX) / 从大兴机场:</span>
                    <span className="font-semibold">¥200-¥280 (US$27-38)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">From Beijing Railway Station / 从北京站:</span>
                    <span className="font-semibold">¥50-¥70 (US$7-10)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">From City Center (Tiananmen) / 从市中心:</span>
                    <span className="font-semibold">¥35-¥50 (US$5-7)</span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  * Prices are estimates based on Didi (Chinese Uber) or standard taxi rates / 价格为基于滴滴或标准出租车的估算价格
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-8">
          <div className="bg-primary-600 text-white px-6 py-4">
            <h2 className="text-2xl font-bold">科室信息 / Department Information</h2>
          </div>
          <div className="p-6">
            <h3 className="font-semibold text-lg mb-3">Cardiology Department / 心血管科</h3>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Department Features / 科室特色:</p>
                <ul className="list-disc list-inside text-gray-700 mt-1 space-y-1">
                  <li>Specializes in cardiovascular diseases / 专治心血管疾病</li>
                  <li>Advanced cardiac surgery capabilities / 先进的心脏手术能力</li>
                  <li>State-of-the-art diagnostic equipment / 先进的诊断设备</li>
                  <li>International medical team / 国际医疗团队</li>
                </ul>
              </div>
              <div>
                <p className="text-sm text-gray-600">Working Hours / 工作时间:</p>
                <p className="font-medium">Monday - Friday: 8:00 AM - 5:00 PM / 周一至周五: 上午8:00 - 下午5:00</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-8">
          <div className="bg-primary-600 text-white px-6 py-4">
            <h2 className="text-2xl font-bold">医生信息 / Doctor Information</h2>
          </div>
          <div className="p-6">
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="mb-4">
                <h3 className="font-bold text-xl mb-2">{doctor.name} / {doctor.nameCn}</h3>
                <p className="text-gray-600">{doctor.title} / {doctor.specialty} Specialist</p>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Specialty / 专长:</p>
                  <p className="font-medium">Coronary Heart Disease, Arrhythmia / 冠心病, 心律失常</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Experience / 经验:</p>
                  <p className="font-medium">25+ years / 25年以上</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Education / 教育:</p>
                  <p className="font-medium">MD, Peking University Health Science Center / 北京大学医学博士</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Languages / 语言:</p>
                  <p className="font-medium">English, Chinese / 英文, 中文</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-8">
          <div className="bg-primary-600 text-white px-6 py-4">
            <h2 className="text-2xl font-bold">挂号指南 / Registration Guide</h2>
          </div>
          <div className="p-6">
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-lg mb-3">Registration Methods / 挂号方式</h3>
                <div className="space-y-3">
                  <div className="flex items-start p-4 bg-blue-50 rounded-lg">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-3">1</div>
                    <div className="flex-grow">
                      <h4 className="font-semibold">Online Registration / 在线挂号</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Download "Beijing Health 114" app / 下载"北京114挂号"APP<br/>
                        Or visit: <a href="http://www.114yygh.com" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">www.114yygh.com</a>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start p-4 bg-blue-50 rounded-lg">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-3">2</div>
                    <div className="flex-grow">
                      <h4 className="font-semibold">Hospital Registration Desk / 医院挂号窗口</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Visit the hospital registration desk / 前往医院挂号窗口<br/>
                        Bring your passport / 携带护照
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start p-4 bg-blue-50 rounded-lg">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-3">3</div>
                    <div className="flex-grow">
                      <h4 className="font-semibold">International Department Direct / 国际部直接预约</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Call: +86-10-6445-6370<br/>
                        Email: anzhen_international@bjmu.edu.cn
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-lg mb-3">Registration Timing / 挂号时间</h3>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                  <div className="space-y-2">
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Online / 在线:</p>
                      <p className="text-sm text-gray-600">7 days in advance / 提前7天开放</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Walk-in / 现场:</p>
                      <p className="text-sm text-gray-600">Same day available (limited slots) / 当天有号(名额有限)</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">International Department / 国际部:</p>
                      <p className="text-sm text-gray-600">14 days in advance / 提前14天开放</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-lg mb-3">Required Documents / 所需文件</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>Valid Passport / 有效护照</li>
                  <li>Medical Records (if available) / 医疗记录(如有)</li>
                  <li>Contact Information / 联系方式</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-8">
          <div className="bg-primary-600 text-white px-6 py-4">
            <h2 className="text-2xl font-bold">附近酒店 / Nearby Hotels</h2>
          </div>
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Hotel / 酒店</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Distance / 距离</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Price / 价格</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Rating / 评分</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {hotels.map((hotel, index) => (
                    <tr key={index}>
                      <td className="px-4 py-4">
                        <p className="font-medium">{hotel.name}</p>
                        <p className="text-sm text-gray-600">{hotel.nameCn}</p>
                      </td>
                      <td className="px-4 py-4 text-gray-700">{hotel.distance}</td>
                      <td className="px-4 py-4 text-gray-700">{hotel.priceRange}</td>
                      <td className="px-4 py-4 text-gray-700">{hotel.rating}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              * Prices are estimates and may vary based on season and availability. Prices shown are per night / 价格为估算值，可能因季节和可用性而变化。所示价格为每晚价格
            </p>
          </div>
        </div>

        <div className="bg-primary-50 border border-primary-200 rounded-lg p-8 text-center mb-8">
          <h2 className="text-2xl font-bold mb-4">Get Your Personalized Medical Guide</h2>
          <p className="text-primary-800 mb-6 max-w-2xl mx-auto">
            This sample shows the format and information you'll receive. Your personalized guide will be tailored to your specific medical needs, preferred location, and requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/services"
              className="btn-primary"
            >
              Get Your Guide for $9.99
            </Link>
            <Link
              to="/hospitals"
              className="btn-secondary"
            >
              Browse Hospitals
            </Link>
          </div>
        </div>

        <div className="text-center text-sm text-gray-500">
          <p>Disclaimer: This guide is for informational purposes only. Please consult with qualified healthcare professionals for medical advice.</p>
          <p className="mt-2">免责声明：本指南仅供参考。请咨询合格的医疗专业人员获取医疗建议。</p>
        </div>
    </div>
  );
};

export default SampleGuide;
